<!-- Approach A (requirement-led): one row per service / sub-service that requires
     a document, with the covering document cards (or an Upload CTA) on the right;
     then an Additional documents bucket. Shared by the vendor Documents tab and
     the internal Users console via [mode]. -->
<div class="doc-reqs" *ngIf="zoneHasDocNodes(serviceZone); else noZoneReqs">
  <!-- Internal (review) surface: license-driven named rows, with the sub-service
       license rows nested in a bordered group under their parent service and the
       service-level license rows on top. Same rows/state as the vendor surface,
       plus the review layer (state pill + Approve / Reject / Revoke / Re-approve)
       the reqRow adds when [mode]=internal. -->
  <ng-container *ngIf="isInternal; else vendorFlat">
    <ng-container *ngFor="let group of zoneLicenseServiceGroups(serviceZone)">
      <ng-container
        *ngFor="let row of group.serviceRows"
        [ngTemplateOutlet]="reqRow"
        [ngTemplateOutletContext]="{ node: row }"
      ></ng-container>
      <div class="doc-req-group" *ngIf="group.subRows.length > 0">
        <div class="doc-req-group__label">{{ group.groupLabel }}</div>
        <ng-container
          *ngFor="let sub of group.subRows"
          [ngTemplateOutlet]="reqRow"
          [ngTemplateOutletContext]="{ node: sub }"
        ></ng-container>
      </div>
    </ng-container>
  </ng-container>

  <!-- Vendor surface: one NAMED row per applicable license requirement (titled by
       the credential), sourced from GET /license-requirements. A service/sub-
       service can yield multiple rows (one per attached license). -->
  <ng-template #vendorFlat>
    <ng-container
      *ngFor="let node of zoneLicenseRows(serviceZone)"
      [ngTemplateOutlet]="reqRow"
      [ngTemplateOutletContext]="{ node: node }"
    ></ng-container>
  </ng-template>
</div>

<!-- One requirement row: a header (aggregate state + name + Upload) and the
     covering-document cards (or just Upload when none). Shared verbatim by the
     vendor flat list and the internal nested groups so both render identically;
     only the per-doc review layer differs by [mode]. -->
<ng-template #reqRow let-node="node">
  <div class="doc-req">
    <ng-container
      *ngIf="{
        st: rowAggregateState(node),
        docs: rowCoveringDocuments(node)
      } as ctx"
    >
      <div class="doc-req__row" [ngClass]="'doc-req--' + ctx.st">
        <div class="doc-req__header">
          <div class="doc-req__info">
            <span
              class="material-icons doc-req__icon"
              [ngClass]="{
                'doc-req__icon--required': ctx.st === 'required',
                'doc-req__icon--review': ctx.st === 'under-review',
                'doc-req__icon--satisfied': ctx.st === 'satisfied'
              }"
            >
              {{
                ctx.st === "required"
                  ? "error_outline"
                  : ctx.st === "under-review"
                    ? "hourglass_empty"
                    : "check_circle"
              }}
            </span>
            <div class="doc-req__text">
              <span class="doc-req__name">{{ node.label }}</span>
              <!-- Vendor license rows: the node + credential scope this named
                   requirement applies to. -->
              <span class="doc-req__context" *ngIf="node.serviceContext">
                {{ node.serviceContext
                }}<ng-container *ngIf="node.zoneContext">
                  · {{ node.zoneContext }}</ng-container
                >
              </span>
              <span
                class="doc-req__state"
                [ngClass]="{
                  'doc-req__state--required': ctx.st === 'required',
                  'doc-req__state--review': ctx.st === 'under-review',
                  'doc-req__state--satisfied': ctx.st === 'satisfied'
                }"
              >
                {{
                  ctx.st === "required"
                    ? "Document required"
                    : ctx.st === "under-review"
                      ? "Under review"
                      : "On file"
                }}
              </span>
            </div>
          </div>
          <!-- Upload action, always in this top-right header spot. Filled navy
               primary, matching the Profile Documents Upload button. Hidden for
               internal read-only managers. -->
          <ng-container *ngIf="showManageActions">
            <button
              type="button"
              class="doc-btn doc-btn--primary doc-req__upload-another"
              [attr.data-testid]="'zone-doc-' + zoneTestKey + '-req-' + rowTestKey(node) + '-upload'"
              (click)="onUpload(node.service, node.subType, node.license)"
            >
              <span class="material-icons">upload_file</span>Upload
            </button>
          </ng-container>
        </div>

        <!-- Body: one card per covering document. -->
        <div class="doc-req__docs" *ngIf="ctx.docs.length > 0">
          <div class="doc-chip" *ngFor="let doc of ctx.docs">
            <span class="material-icons doc-chip__file-icon">description</span>
            <div class="doc-chip__body">
              <div class="doc-chip__name" [title]="doc.fileName">
                {{ doc.fileName }}
              </div>
              <div class="doc-chip__meta" *ngIf="doc.issueDate">
                <span class="material-icons">today</span>
                Issued {{ doc.issueDate | date: "MMM d, y" }}
              </div>
              <div
                class="doc-chip__meta"
                *ngIf="doc.expirationDate"
                [class.doc-chip__meta--expired]="isExpired(doc)"
              >
                <span class="material-icons">event</span>
                Expires {{ doc.expirationDate | date: "MM/dd/yyyy" }}
              </div>
              <!-- Vendor cue for non-rejected states (never exposes the
                   approve/reject decision). A REJECTED doc instead gets the
                   state pill + reason below — Product wants PAs to see that a
                   license was rejected, and why. -->
              <div
                *ngIf="!isInternal && reviewStatusOf(doc) !== 'REJECTED'"
                class="doc-chip__cue"
                [ngClass]="'doc-chip__cue--' + docCueKind(doc)"
              >
                {{ docCue(doc) }}
              </div>
              <!-- State pill + reject reason: the full internal review surface,
                   AND the PA when the document was REJECTED. -->
              <ng-container
                *ngIf="isInternal || reviewStatusOf(doc) === 'REJECTED'"
                [ngTemplateOutlet]="stateLayer"
                [ngTemplateOutletContext]="{ doc: doc }"
              ></ng-container>
            </div>
            <div class="doc-chip__actions">
              <button
                *ngIf="isPdf(doc)"
                type="button"
                class="doc-icon-btn"
                title="Preview"
                [attr.data-testid]="'zone-doc-' + zoneTestKey + '-req-' + rowTestKey(node) + '-doc-' + docTestKey(doc) + '-preview'"
                (click)="onPreview(doc)"
              >
                <span class="material-icons">visibility</span>
              </button>
              <button
                type="button"
                class="doc-icon-btn"
                title="Download"
                [attr.data-testid]="'zone-doc-' + zoneTestKey + '-req-' + rowTestKey(node) + '-doc-' + docTestKey(doc) + '-download'"
                (click)="onDownload(doc)"
              >
                <span class="material-icons">file_download</span>
              </button>
              <ng-container *ngIf="showManageActions">
                <button
                  type="button"
                  class="doc-icon-btn"
                  title="Edit"
                  [attr.data-testid]="'zone-doc-' + zoneTestKey + '-req-' + rowTestKey(node) + '-doc-' + docTestKey(doc) + '-edit'"
                  (click)="onEditCoverage(doc)"
                >
                  <span class="material-icons">edit</span>
                </button>
                <button
                  type="button"
                  class="doc-icon-btn doc-icon-btn--danger"
                  title="Delete"
                  [attr.data-testid]="'zone-doc-' + zoneTestKey + '-req-' + rowTestKey(node) + '-doc-' + docTestKey(doc) + '-delete'"
                  (click)="onDelete(doc)"
                >
                  <span class="material-icons">delete</span>
                </button>
              </ng-container>
              <!-- Internal review affordances (MANAGE only). -->
              <ng-container
                *ngIf="isInternal && showManageActions"
                [ngTemplateOutlet]="reviewButtons"
                [ngTemplateOutletContext]="{ doc: doc }"
              ></ng-container>
            </div>
          </div>
        </div>
      </div>
    </ng-container>
  </div>
</ng-template>
<ng-template #noZoneReqs>
  <p class="doc-empty">No documents required for this zone.</p>
</ng-template>

<!-- Zone documents not covering any displayed required node. -->
<div
  class="doc-additional"
  *ngIf="additionalZoneDocuments(serviceZone).length > 0"
>
  <div class="doc-additional__label">
    Additional documents — not tied to a requirement
  </div>
  <div class="doc-cards">
    <div
      class="doc-card"
      *ngFor="let doc of additionalZoneDocuments(serviceZone)"
    >
      <span class="material-icons doc-card__file-icon">description</span>
      <div class="doc-card__body">
        <div class="doc-card__name" [title]="doc.fileName">
          {{ doc.fileName }}
        </div>
        <div
          class="doc-card__meta"
          *ngIf="doc.expirationDate"
          [class.doc-card__meta--expired]="isExpired(doc)"
        >
          <span class="material-icons">event</span>
          Expires {{ doc.expirationDate | date: "MM/dd/yyyy" }}
        </div>
        <!-- State pill + reject reason: the full internal review surface, AND
             the PA when the document was REJECTED (Product: PAs see rejections). -->
        <ng-container
          *ngIf="isInternal || reviewStatusOf(doc) === 'REJECTED'"
          [ngTemplateOutlet]="stateLayer"
          [ngTemplateOutletContext]="{ doc: doc }"
        ></ng-container>
        <ng-container *ngIf="showManageActions">
          <button
            type="button"
            class="doc-link"
            [attr.data-testid]="'zone-doc-' + zoneTestKey + '-additional-' + docTestKey(doc) + '-link'"
            (click)="onEditCoverage(doc)"
          >
            <span class="material-icons">edit</span>Link to a Requirement
          </button>
        </ng-container>
      </div>
      <div class="doc-card__actions">
        <button
          *ngIf="isPdf(doc)"
          type="button"
          class="doc-icon-btn"
          title="Preview"
          [attr.data-testid]="'zone-doc-' + zoneTestKey + '-additional-' + docTestKey(doc) + '-preview'"
          (click)="onPreview(doc)"
        >
          <span class="material-icons">visibility</span>
        </button>
        <button
          type="button"
          class="doc-icon-btn"
          title="Download"
          [attr.data-testid]="'zone-doc-' + zoneTestKey + '-additional-' + docTestKey(doc) + '-download'"
          (click)="onDownload(doc)"
        >
          <span class="material-icons">file_download</span>
        </button>
        <ng-container *ngIf="showManageActions">
          <button
            type="button"
            class="doc-icon-btn doc-icon-btn--danger"
            title="Delete"
            [attr.data-testid]="'zone-doc-' + zoneTestKey + '-additional-' + docTestKey(doc) + '-delete'"
            (click)="onDelete(doc)"
          >
            <span class="material-icons">delete</span>
          </button>
        </ng-container>
        <!-- Internal review affordances (MANAGE only). -->
        <ng-container
          *ngIf="isInternal && showManageActions"
          [ngTemplateOutlet]="reviewButtons"
          [ngTemplateOutletContext]="{ doc: doc }"
        ></ng-container>
      </div>
    </div>
  </div>
</div>

<!-- Internal-only: the review-state pill + surfaced reject reason for a doc. -->
<ng-template #stateLayer let-doc="doc">
  <div
    class="doc-state-pill"
    [ngClass]="'doc-state-pill--' + docReviewState(doc)"
  >
    {{ reviewStateLabel(docReviewState(doc)) }}
  </div>
  <div
    class="doc-reject-reason"
    *ngIf="reviewStatusOf(doc) === 'REJECTED' && doc.rejectReason"
  >
    <span class="material-icons">info</span>
    {{ doc.rejectReason }}
  </div>
</ng-template>

<!-- Internal-only: the review actions for a doc, keyed off its raw decision.
     Approve is one click; Reject / Revoke prompt the host for a reason. -->
<ng-template #reviewButtons let-doc="doc">
  <ng-container [ngSwitch]="reviewStatusOf(doc)">
    <ng-container *ngSwitchCase="'APPROVED'">
      <button
        type="button"
        class="doc-icon-btn doc-icon-btn--danger"
        title="Revoke approval"
        (click)="onReview(doc, 'revoke')"
      >
        <span class="material-icons">undo</span>
      </button>
    </ng-container>
    <ng-container *ngSwitchCase="'REJECTED'">
      <button
        type="button"
        class="doc-icon-btn doc-icon-btn--approve"
        title="Re-approve"
        (click)="onReview(doc, 'reapprove')"
      >
        <span class="material-icons">check</span>
      </button>
    </ng-container>
    <ng-container *ngSwitchDefault>
      <button
        type="button"
        class="doc-icon-btn doc-icon-btn--approve"
        title="Approve"
        (click)="onReview(doc, 'approve')"
      >
        <span class="material-icons">check</span>
      </button>
      <button
        type="button"
        class="doc-icon-btn doc-icon-btn--danger"
        title="Reject"
        (click)="onReview(doc, 'reject')"
      >
        <span class="material-icons">close</span>
      </button>
    </ng-container>
  </ng-container>
</ng-template>
